<script setup>
/**
 * PageEditorDetachFromPageBtn
 * Disconnects a content record from the current page layout.
 * Logic:
 * - Only visible in 'pageEditor' mode for 'content' tables.
 * - Hidden for global areas (Header/Footer) to prevent accidental detachment of site-wide layout elements.
 */
import {computed} from 'vue'
import {useRoute} from 'vue-router'
import FormSidebarBtn from "./FormSidebarBtn.vue"
import {usePageEditorStore} from '@/stores/pageEditor'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

// Composables
const route = useRoute()
const pageEditorStore = usePageEditorStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const tableName = computed(() => formStore.value?.tableName)

const isPageEditor = computed(() => route.name === 'pageEditor')

const pageContentId = computed(() => pageEditorStore.activePageContentId)

const pageContent = computed(() => {
    return pageEditorStore.contentItems.find(o => o.pageContentId === pageContentId.value)
})

const isHeaderOrFooterContent = computed(() => {
    const area = pageContent.value?.contentAreaName
    return area === 'Page header' || area === 'Page footer'
})

const show = computed(() => {
    return (
        isPageEditor.value &&
        tableName.value === 'content' &&
        !isHeaderOrFooterContent.value
    )
})

// --- Methods ---

const onClick = () => {
    pageEditorStore.detachContent(pageContentId.value)
}
</script>

<template>
    <FormSidebarBtn
        v-if="show"
        class="text-warning-emphasis"
        @click.prevent="onClick"
    >
        <i class="me-2 bi bi-x-lg"/>
        Detach from page
    </FormSidebarBtn>
</template>